<template>
  <div class="ur-login">
    <!-- ── Main body ── -->
    <div class="ur-login-body">
      <div class="ur-login-grid">

        <!-- Left panel: logo + welcome -->
        <div class="ur-login-left">
          <img :src="logoUrl" class="ur-login-logo-img" alt="logo" />
          <div class="ur-login-welcome">{{ $t('URBANRENEWAL_ADMIN.LOGIN.WELCOME') }}</div>
        </div>

        <!-- Right panel: login card -->
        <div class="ur-login-card">
          <h2 class="ur-login-card-title">{{ $t('URBANRENEWAL_ADMIN.LOGIN.TITLE') }}</h2>

          <!-- Error message -->
          <div v-if="errorMsg" class="ur-login-error">{{ errorMsg }}</div>

          <!-- Account field -->
          <div class="ur-login-field">
            <label class="ur-login-field-label">{{ $t('URBANRENEWAL_ADMIN.LOGIN.ACCOUNT') }}</label>
            <div class="ur-login-field-row">
              <span class="ur-login-field-icon">
                <!-- Person icon -->
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="8" r="4"/>
                  <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                </svg>
              </span>
              <input
                class="ur-login-field-input"
                type="text"
                v-model="username"
                autocomplete="username"
                @keyup.enter="doLogin"
                @input="errorMsg = ''"
              />
            </div>
          </div>

          <!-- Password field -->
          <div class="ur-login-field ur-login-field-pwd">
            <label class="ur-login-field-label">{{ $t('URBANRENEWAL_ADMIN.LOGIN.PASSWORD') }}</label>
            <div class="ur-login-field-row">
              <span class="ur-login-field-icon">
                <!-- Lock icon -->
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="5" y="11" width="14" height="10" rx="2"/>
                  <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
                </svg>
              </span>
              <input
                class="ur-login-field-input"
                :type="showPwd ? 'text' : 'password'"
                v-model="password"
                autocomplete="current-password"
                @keyup.enter="doLogin"
                @input="errorMsg = ''"
              />
              <button class="ur-login-toggle-btn" type="button" @click="togglePwd" tabindex="-1">
                <!-- Eye open -->
                <svg v-if="showPwd" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <!-- Eye closed -->
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Submit button -->
          <button
            class="ur-login-submit-btn"
            type="button"
            :disabled="!canSubmit"
            @click="doLogin"
          >
            {{ $t('URBANRENEWAL_ADMIN.LOGIN.SUBMIT') }}
          </button>

          <!-- Forgot password -->
          <div class="ur-login-forgot-wrap">
            <button class="ur-login-forgot-link" type="button" @click.prevent="openForgetPassword">
              {{ $t('URBANRENEWAL_ADMIN.LOGIN.FORGOT') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Footer ── -->
    <footer class="ur-login-footer">
      {{ $t('URBANRENEWAL_ADMIN.LOGIN.COPYRIGHT') }}
      <span class="ur-login-footer-sep">|</span>
      <a class="ur-login-footer-link" href="/docs/DPF.pdf" target="_blank" rel="noopener">Data Processing Agreement</a>
    </footer>

    <!-- ── Forget password dialog ── -->
    <v-dialog v-model="forgetPasswordVisible" max-width="500">
      <forget-password-dialog
        @emitForgetPasswordDialog="onForgetPasswordDialog"
        @emitForgetPasswordSuccess="onForgetPasswordSuccess"
      />
    </v-dialog>

    <!-- ── Success dialog ── -->
    <v-dialog v-model="successVisible" width="360" content-class="statusDialog">
      <success-dialog @emitSuccessDialog="successVisible = false" />
    </v-dialog>
  </div>
</template>

<script>
import { logoutUser } from '@/services/api/auth';
import forgetPasswordDialog from '@/components/Dialog/User/forgetPasswordDialog';
import successDialog from '@/components/Dialog/successDialog';
import LoginValidationService from '@/services/LoginValidationService';

export default {
  name: 'UrbanRenewalAdminLogin',

  components: {
    forgetPasswordDialog,
    successDialog,
  },

  data() {
    return {
      username: '',
      password: '',
      showPwd: false,
      errorMsg: '',
      submitting: false,
      forgetPasswordVisible: false,
      successVisible: false,
      logoUrl: require('@/assets/images/UPGPT.png'),
    };
  },

  mounted() {
    if (this.tenant) {
      LoginValidationService.getUrbanRenewalLogo(this.tenant)
        .then(res => {
          if (res.data && res.data.logo_url) {
            this.logoUrl = res.data.logo_url;
          }
        })
        .catch(() => {});
    }
  },

  computed: {
    canSubmit() {
      return this.username.trim() && this.password.trim() && !this.submitting;
    },
    tenant() {
      return this.$route.query.tenant || window.localStorage.getItem('tenant') || '';
    },
  },

  methods: {
    async doLogin() {
      if (!this.username.trim() || !this.password.trim()) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.LOGIN.ERR_REQUIRED');
        return;
      }
      if (!this.tenant) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.LOGIN.ERR_NO_TENANT');
        return;
      }
      this.submitting = true;
      try {
        await this.$store.dispatch('login', {
          username: this.username,
          password: this.password,
          tenant: this.tenant,
        });
        if (window.localStorage.getItem('is_staff') === 'true') {
          this.$emit('loginSuccess');
          this.$router.push('/urbanrenewal/admin/basicdata');
        } else {
          this.errorMsg = this.$t('URBANRENEWAL_ADMIN.LOGIN.ERR_NO_ACCESS');
          logoutUser();
        }
      } catch (e) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.LOGIN.ERR_INVALID');
      } finally {
        this.submitting = false;
      }
    },

    togglePwd() {
      this.showPwd = !this.showPwd;
    },

    openForgetPassword() {
      this.forgetPasswordVisible = true;
    },

    onForgetPasswordDialog(val) {
      this.forgetPasswordVisible = val;
    },

    onForgetPasswordSuccess(val, errorMsg) {
      if (val) {
        this.forgetPasswordVisible = false;
        this.successVisible = true;
      } else {
        this.errorMsg = errorMsg;
      }
    },
  },
};
</script>
